/**
 * Used inside the file-drop in order to handle the uploading of the actual files
 * as well as the progress bar animation while the file(s) is being upload.
 * NOTE: This needs to be used as a part of file-drop.js
 *
 * Events:
 * - start: triggered right before an upload first begins
 * - done: when an upload finishes
 * - error: when an upload causes an error of some sort (e.g. invalid file)
 * - reset: when the current file was cleared out to make room for a new upload
 *
 * For a good tutorial on file uploads in general:
 * https://css-tricks.com/drag-and-drop-file-uploading/
 */

import $ from 'jquery'

import _ from 'lodash'
import Backbone from 'backbone'
import template from './file-uploader.hbs'
import momentHelper from 'scoreboard/components/moment-helper'
import icon from 'scoreboard/components/icon'
import fileAttachmentContent from 'scoreboard/components/files/file-attachment-content.hbs'
import moment from 'moment'
import fileInfo from 'scoreboard/components/files/file-info'
import fileExtensionValidatorFactory from 'scoreboard/components/validators/file-extension-validator-factory'
import fileAttachment from 'scoreboard/components/files/file-attachment.hbs'
import {isMissingColumnsException} from 'scoreboard/imports/new-column-name-chooser-wizard'

var FileUploader = Backbone.View.extend({

	className: 'file-uploader',

	events: {
		'click .reset': 'reset',
		'change input[type=file]': 'onFileChosen'
	},

	// Classes which are applied to the file-uploader which act on the views
	// which take their turns showing themselves in the component depending on
	// what state the file being uploaded is in.
	states: {
		UPLOADING: 'file-uploader-state-is-uploading',
		ERROR: 'file-uploader-state-has-error'
	},

	// Views is an array which describes the actual markup that is swapped in and
	// out of view in the file-uploader as the file goes through its various upload states.
	views: [{
		detailIcon: 'docs-document',
		class: 'file-uploader-view-uploading',
		hasFileInfo: true
	}],

	/**
	 * @typedef FileUploaderOptions
	 *
	 * @property {Object} options
	 *
	 * @property {String} options.url the URL to submit to
	 *
	 * @property {Function} [options.dataFn] callback creating the data to
	 * be submitted to the server
	 *
	 * @property {Array} [options.files] files dropped into a drag and
	 * drop area or previously uploaded
	 *
	 * @property {String[]} [options.accept] restrictions on the types of
	 * files that can be uploaded. Must be one or more of the keys from
	 * fileUploader.accepts, e.g. ['spreadsheet', 'spreadsheetWithoutNumbers']
	 *
	 * @property {String} [options.icon] icon to use when uploading a
	 * file, e.g. 'spreadsheet'
	 *
	 * @property {Object} [options.lastModified=moment()] the last modified date
	 * to display for the file.  We don't want to use the date from the user's
	 * file system, and instead want to use the date the file was uploaded to
	 * Scoreboard
	 *
	 * @property {Boolean} [options.alreadyUploaded] indicates the file was already
	 * uploaded and the upload is fully complete
	 *
	 * @property {Boolean} [options.manualUpload=false] tells the file uploader not to upload
	 * the file as soon as it is selected. `upload()` will have to be called manually
	 */

	/**
	 * Creates a file uploader control which handles the file upload process as
	 * well as a visual representation of the file being uploaded.
	 *
	 * @constructs
	 * @param {FileUploaderOptions} options
	 */
	initialize: function(options) {
		this.options = options || {}
		if (!_.get(options, 'url')) throw Error('must provide a url')
		this.files = this.options.files
		this.stateViews = []
		this.views.forEach(obj => this.stateViews.push(fileAttachmentContent(obj)))
		_.bindAll(this, 'resize')
		$(window).on('resize', () => this.resize())
	},

	render: function() {
		this.$el.empty().append(template({
			accept: this.acceptStr(),
			hasFiles: !this.isFileUnspecified()
		}))
		this.$('.file-attachment').replaceWith(fileAttachment())
		// Add the various detail views to the progress bar portion of the control
		_.each(this.stateViews, view => this.$('.file-attachment-content').append(view))

		if (!this.isFileUnspecified()) {
			this.specifyFile()
			if (this.options.alreadyUploaded) {
				this.$('.progress').css('width', '100%')
			} else {
				// #31151 FormView.submitButtons() has a nextTick() that changes
				// the button state, so if we want the "uploading" state to stick,
				// we unfortunately need to set it on nextTick, too
				process.nextTick(() => this.upload())
			}
		}

		if (this.options.icon) {
			this.$('.file-icon').html(icon(this.options.icon))
		}

		return this
	},

	/**
	 * Returns the correct value for "accept" attribute of <input type="file">
	 * based on the value of this.options.accept
	 */
	acceptStr() {
		if (_.isEmpty(this.options.accept)) {
			return null
		}

		return _(this.options.accept)
			.map(type => fileInfo.accepts[type])
			.join(',')
	},


	progress: function(percent) {
		this.$('.progress').css('width', percent + '%')
	},

	chooseFile: function() {
		if (!this.isFileUnspecified()) {
			return
		}
		// simulate clicking on the hidden file uploader so the user can locate the
		// file to upload
		this.$('input[type=file]').click()
	},

	// executed once a file is specified
	onFileChosen: function(e) {
		if (e.target.files) {
			// We don't want to use the date from the user's file system,
			// and instead want to use the date the file was uploaded
			this.lastModified = this.options.lastModified || moment()
		}
		if (!this.specifyFile(e)) {
			return
		}

		// sometimes after clicking Browse and then Cancel, you end up here
		// with no files
		if (this.isFileUnspecified()) {
			return
		}

		// begin the upload
		if (!this.options.manualUpload) {
			this.upload()
		}

		this.trigger('fileChosen')
	},

	isFileUnspecified: function() {
		// #14377 don't use isEmpty on a FileList in IE
		return _.isNil(this.files) || this.files.length === 0
	},

	/**
	 * Validates whether the current file has an allowed extension, and displays
	 * an error tooltip if the extension isn't allowed
	 *
	 * @return {Boolean} true if the extension was valid
	 */
	validateExtension() {
		let validator = fileExtensionValidatorFactory(this.options.accept)

		let error = validator(this.file.name)
		if (error) {
			this.trigger('extensionError', error)
		}

		return !error
	},

	/**
	 * Handles the immediate aftermath of a user specifying a file. Validates
	 * the file extension and updates the UI to show the file name.
	 * @param {Event} [e] drop event, if applicable
	 * @returns {boolean} whether a valid file was specified
	 */
	specifyFile: function(e) {
		// after a DND upload, we won't be responding to an event.  instead
		// we'll use the files that were passed in when this view was created
		this.files = e ? e.target.files : this.files
		this.file = this.files[0]

		// sometimes after triggering the file tray and then hitting Cancel, you end up here
		// with no files.
		if (this.isFileUnspecified() || !this.validateExtension()) {
			delete this.files
			return false
		}

		// this can happen if the user uploads a file, then triggers the system tray, then
		// hits cancel
		if (this.file) {
			const lastModified = this.lastModified || this.file.lastModified
			this.views.forEach(state => {
				if (state.hasFileInfo) {
					this.$(`.${state.class} .label`).text(this.file.name)
					this.$(`.${state.class} .sub-label`).text(momentHelper.formatDateTime(lastModified))
				}
			})
			this.$('[type=file]').addClass('has-files')
		}

		this.$('.upload').hide()
		this.$('.file-attachment').show()
		return true
	},

	getFile: function() {
		return _.isEmpty(this.files) ? null : this.files[0]
	},

	resetAppearance() {
		const isMissingColumnsEx = isMissingColumnsException(this.xhr)
		_.invoke(this.xhr, 'abort')

		// #58698 if this is a missing columns exception it will have been handled by
		// upload-form.js and there will be a next step in the wizard, so we don't want
		// to reset the appearance or do any other error cleanup on the file-uploader
		if (isMissingColumnsEx) return

		this.$('.upload').show()
		this.$('.file-attachment').hide()
		this.$('.file-attachment .progress').css('width', 0)
		this.$('[name=file]').val('')
		this.$('[type=file]')[0].value = null
		this.$('[type=file]').removeClass('has-files')
		this.removeStateClasses()
		delete this.state
		delete this.file
		delete this.files
	},

	// reset the file uploader so another upload can be submitted
	reset: function(e) {
		// prevent a file uploader being automatically triggered
		e && e.preventDefault()
		e && e.stopPropagation()
		this.resetAppearance()
		this.trigger('reset')
	},

	// upload the file
	upload: function() {
		// sometimes after triggering the system tray and then Cancel, you end up here
		// with no files.
		if (this.isFileUnspecified()) {
			return
		}

		// trigger right before the upload begins
		this.trigger('start', this.files)
		this.setState(this.states.UPLOADING)
		this.postFile.apply(this, arguments)
	},

	postFile: function () {
		var data = _.invoke(this.options, 'dataFn', this.files)
		var form = new FormData()
		form.append('file', this.files[0])
		_.forIn(data, function (value, key) {
			form.append(key, value)
		})

		this.xhr = $.ajax({
			url: _.result(this.options, 'url'),
			method: 'POST',
			data: form,
			contentType: false,
			processData: false,
			xhr: this._xhr.bind(this),
			success: this.uploadFinished.bind(this),
			error: this.error.bind(this)
		})
	},

	// constructors an XHR that lets us listen to the progress of a file upload
	// adapted from the "optionXhr" variable in arale-upload:index.js:submit
	_xhr: function() {
		var xhr = $.ajaxSettings.xhr()
		xhr.upload.addEventListener('progress', function(event) {
			var percent = 0
			if (event.lengthComputable) {
				percent = Math.ceil(event.loaded / event.total * 100)
			}
			this.progress(percent)
		}.bind(this))
		return xhr
	},

	// executed when upload is done
	uploadFinished: function(data) {
		if (!_.isNil(data) && _.isString(data)) {
			data = '' == data ? {} : JSON.parse(data)
		}

		delete this.xhr
		this.trigger('done', data)
	},

	applyNewState: function(newState)  {
		this.removeStateClasses()
		_.forIn(this.states, (s) => {
			if (newState == s) {
				this.$el.addClass(s)
				this.state = s
				return
			}
		})
	},

	setState: function(state) {
		if (this.state == state) {
			return
		}

		var visibleDetail
		this.$('.file-uploader-state').each((i, el) => {
			if ($(el).css('opacity') == '1') {
				visibleDetail = $(el)
			}
		})

		// If there's a visible .file-uploader-state, wait till it finishes
		// transitioning in order to show the next state
		if (visibleDetail) {
			visibleDetail.one('transitionend', () => this.applyNewState(state))
			this.removeStateClasses()
		// If not, make the next state visible immediately.
		} else {
			this.removeStateClasses()
			this.$el.addClass('disable-transitions')
			this.applyNewState(state)
			// Wait an extra tick to ensure the .disable-transitions class is not
			// removed before the new class is applied in this.applyNewState(). That's possible
			// when setState() is called when xhr events are fired (ex. 'success')
			process.nextTick(() => this.$el.removeClass('disable-transitions'))
		}
	},

	removeStateClasses: function() {
		// Remove any .file-uploader-state-* classes from the root element
		this.$el.removeClass((index, className) => {
			return (className.match(/(^|\s)file-uploader-state-\S+/g) || []).join(' ')
		})
	},

	error: function(xhr) {
		this.trigger('error', xhr)
		// #14820 clear the file uploader so the same file can be uploaded again
		this.$('[type=file]').val('')
	},

	onDrop(e) {
		this.files = _.result(e, 'originalEvent.dataTransfer.files')
		if (this.specifyFile()) {
			this.upload()
		}
	},

	/**
	 * Cancels the current file upload
	 */
	cancel: function() {
		this.xhr && this.xhr.abort()
	},

	resize: function() {
		// Ensure the .file-uploader-state-uploading labels do not
		// overlap or bleed outside the process bar. The width is maintained here
		// in the js because they're absolutely positioned
		this.$('.file-uploader-view-uploading .file-attachment-details').css('max-width', () => {
			let svgWidth = this.$('.file-uploader-view-uploading svg').width() || 0
			return this.$el.width() - (60 + svgWidth) // 60px = space for padding
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		$(window).off('resize', this.resize)
	}
})

export default FileUploader

FileUploader.__filename__ = 'components/files/file-uploader.js'
